import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, EnvelopeSimple, MapPin, InstagramLogo } from '@phosphor-icons/react';
import { CONTACT_INFO } from '../App';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const isContactPage = location.pathname === '/contact';
  const isLocationPage = location.pathname === '/location-partenaires';
  const isProjetPage = location.pathname === '/projet';
  const hideCTA = isContactPage || isLocationPage || isProjetPage;

  return (
    <footer data-testid="main-footer" className="bg-black border-t border-white/10">
      {/* Single CTA Section - Hidden on Contact, Location and Projet pages */}
      {!hideCTA && (
        <div className="border-b border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 text-center">
            <h3 className="font-display text-2xl lg:text-4xl text-white mb-4">
              Concevons ensemble l'exception
            </h3>
            <div className="w-16 h-px bg-white/30 mx-auto mb-8"></div>
            <p className="text-white/60 mb-8 max-w-xl mx-auto">
              Nous vous accompagnons dans la conception et la mise en place de votre bar.
            </p>
            
            {/* Contact Info Row */}
            <div className="flex flex-wrap justify-center items-center gap-6 md:gap-8 mb-10 text-sm">
              <a 
                href={`tel:${CONTACT_INFO.phone.replace(/\s/g, '')}`}
                className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
              >
                <Phone size={18} weight="light" className="text-white" />
                <span>{CONTACT_INFO.phone}</span>
              </a>
              <a 
                href={`mailto:${CONTACT_INFO.email}`}
                className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
              >
                <EnvelopeSimple size={18} weight="light" className="text-white" />
                <span>{CONTACT_INFO.email}</span>
              </a>
              <div className="flex items-center gap-2 text-white/60">
                <MapPin size={18} weight="light" className="text-white" />
                <span>{CONTACT_INFO.city}</span>
              </div>
              <a 
                href="https://www.instagram.com/studiobar.paris/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
              >
                <InstagramLogo size={18} weight="light" className="text-white" />
                <span>studiobar.paris</span>
              </a>
            </div>
            
            <Link
              to="/projet"
              data-testid="footer-cta"
              className="btn-primary"
            >
              Parlez-nous de votre projet
            </Link>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16">
          {/* Brand Column */}
          <div className="md:col-span-4">
            <Link to="/" className="inline-block">
              <div className="h-16 w-16" style={{ 
                WebkitMaskImage: 'url(https://customer-assets.emergentagent.com/job_signature-studio-4/artifacts/e2epx8vp_logo%20studiobar.png)',
                WebkitMaskSize: 'contain',
                WebkitMaskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                maskImage: 'url(https://customer-assets.emergentagent.com/job_signature-studio-4/artifacts/e2epx8vp_logo%20studiobar.png)',
                maskSize: 'contain',
                maskRepeat: 'no-repeat',
                maskPosition: 'center',
                backgroundColor: 'white'
              }} />
            </Link>
            <p className="mt-4 text-sm text-white/50 leading-relaxed">
              Des expériences cocktail scénographiées pour vos événements.
            </p>
            <p className="mt-4 text-sm text-white/70 font-medium">
              Nos bars peuvent être ajustés en dimensions selon votre espace et votre projet.
            </p>
          </div>

          {/* Navigation */}
          <div className="md:col-span-4">
            <h4 className="text-white text-sm uppercase tracking-wider mb-3">Navigation</h4>
            <div className="w-12 h-px bg-white/30 mb-5"></div>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-sm text-white/50 hover:text-white transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/concept" className="text-sm text-white/50 hover:text-white transition-colors">
                  Concept
                </Link>
              </li>
              <li>
                <Link to="/bar-mobile" className="text-sm text-white/50 hover:text-white transition-colors">
                  Bar Mobile
                </Link>
              </li>
              <li>
                <Link to="/studio-crea" className="text-sm text-white/50 hover:text-white transition-colors">
                  Studio Créa
                </Link>
              </li>
              <li>
                <Link to="/prestation-cocktail" className="text-sm text-white/50 hover:text-white transition-colors">
                  Prestation Cocktail
                </Link>
              </li>
              <li>
                <Link to="/location-partenaires" className="text-sm text-white/50 hover:text-white transition-colors">
                  Location & Partenaires
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm text-white/50 hover:text-white transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <h4 className="text-white text-sm uppercase tracking-wider mb-3">Contact</h4>
            <div className="w-12 h-px bg-white/30 mb-5"></div>
            <address className="not-italic space-y-4 text-sm">
              <div className="flex items-start gap-3 text-white/50">
                <MapPin size={18} weight="light" className="text-white flex-shrink-0 mt-0.5" />
                <div>
                  <p>{CONTACT_INFO.address}</p>
                  <p>{CONTACT_INFO.city}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={18} weight="light" className="text-white flex-shrink-0" />
                <a href={`tel:${CONTACT_INFO.phone.replace(/\s/g, '')}`} className="text-white/50 hover:text-white transition-colors">
                  {CONTACT_INFO.phone}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <EnvelopeSimple size={18} weight="light" className="text-white flex-shrink-0" />
                <a href={`mailto:${CONTACT_INFO.email}`} className="text-white/50 hover:text-white transition-colors">
                  {CONTACT_INFO.email}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <InstagramLogo size={18} weight="light" className="text-white flex-shrink-0" />
                <a 
                  href="https://www.instagram.com/studiobar.paris/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white/50 hover:text-white transition-colors"
                >
                  @studiobar.paris
                </a>
              </div>
            </address>
          </div>
        </div>

        {/* Bottom Bar - Line below text */}
        <div className="mt-12 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/30 text-xs">
            © {currentYear} Studio Bar. Tous droits réservés.
          </p>
          <div className="flex gap-6 text-xs text-white/30">
            <a href="#" className="hover:text-white/60 transition-colors">Mentions Légales</a>
            <a href="#" className="hover:text-white/60 transition-colors">Politique de Confidentialité</a>
          </div>
        </div>
        <div className="mt-8 pt-0 border-t border-white/10"></div>
      </div>
    </footer>
  );
};

export default Footer;
